import { GHL_CALENDAR_EMBED_URL, HOST_NAME, WHATSAPP_URL } from './config';
import { getLead, saveLead } from './pixel';

/* ============ CALENDARIO GHL ============
 * Qué necesitas en GoHighLevel (Calendars → tu calendario → Share/Embed):
 * 1) Copia el link del widget, ej: https://api.leadconnectorhq.com/widget/booking/XXXXXXXX
 *    y pégalo en GHL_CALENDAR_EMBED_URL (funnel/config.ts).
 * 2) Confirmation → "Redirect to URL": <tu-dominio>/#/confirmada
 * 3) El calendario recibe el nombre, email y teléfono por URL, así el formulario de reserva
 *    ya aparece lleno y la cita queda en el MISMO contacto que entró en el paso 1.
 */

export function splitName(full: string) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}

export function calendarSrc() {
  if (!GHL_CALENDAR_EMBED_URL) return '';
  const lead = getLead();
  const { first, last } = splitName(lead.name);
  const params = new URLSearchParams();
  if (first) params.set('first_name', first);
  if (last) params.set('last_name', last);
  if (lead.name) params.set('full_name', String(lead.name));
  if (lead.email) params.set('email', String(lead.email));
  if (lead.phone) params.set('phone', String(lead.phone));
  const qs = params.toString();
  if (!qs) return GHL_CALENDAR_EMBED_URL;
  return GHL_CALENDAR_EMBED_URL + (GHL_CALENDAR_EMBED_URL.includes('?') ? '&' : '?') + qs;
}

// Script oficial de GHL para que el iframe ajuste su altura automáticamente
export function loadGhlEmbedScript() {
  if (document.getElementById('ghl-form-embed')) return;
  const s = document.createElement('script');
  s.id = 'ghl-form-embed';
  s.src = 'https://link.msgsndr.com/js/form_embed.js';
  s.async = true;
  document.body.appendChild(s);
}

/* ============ FECHA DE LA CITA ============
 * La página #/confirmada muestra la fecha si llega por URL (?fecha=2026-10-14T20:30:00-04:00)
 * o si el calendario la avisó al reservar. Si no hay fecha, muestra un texto genérico.
 */
export function bookedStart(): Date | null {
  const hash = window.location.hash;
  const q = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams(window.location.search);
  const raw = q.get('fecha') || q.get('start_time') || q.get('startTime') || getLead().booked_at || '';
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function formatBooked(d: Date) {
  const day = new Intl.DateTimeFormat('es-US', { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  const time = new Intl.DateTimeFormat('es-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d);
  return { day: day.charAt(0).toUpperCase() + day.slice(1), time };
}

// Escucha el aviso de reserva del iframe de GHL (respaldo de la redirección)
export function listenForBooking(onBooked: () => void) {
  const handler = (e: MessageEvent) => {
    let text = '';
    try { text = typeof e.data === 'string' ? e.data : JSON.stringify(e.data); } catch { return; }
    if (!/booking[-_ ]?complete|appointment[-_ ]?booked|msgsndr-booking-complete/i.test(text)) return;
    const m = text.match(/"(?:startTime|start_time|selectedSlot)"\s*:\s*"([^"]+)"/);
    if (m) saveLead({ booked_at: m[1] });
    onBooked();
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

/* ============ AGREGAR A MI CALENDARIO ============ */
const pad = (n: number) => String(n).padStart(2, '0');
const toUtc = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;

const EVENT_TITLE = `Llamada 1:1 con ${HOST_NAME}`;
const EVENT_DETAILS = `Conéctate desde computadora, en un lugar tranquilo. Confirma tu asistencia por WhatsApp: ${WHATSAPP_URL}`;

export function googleCalendarUrl(start: Date, minutes = 30) {
  const end = new Date(start.getTime() + minutes * 60000);
  const p = new URLSearchParams({ action: 'TEMPLATE', text: EVENT_TITLE, dates: `${toUtc(start)}/${toUtc(end)}`, details: EVENT_DETAILS });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function downloadIcs(start: Date, minutes = 30) {
  const end = new Date(start.getTime() + minutes * 60000);
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Chale Rodriguez//Llamada//ES', 'BEGIN:VEVENT',
    `UID:${Date.now()}@bychale`, `DTSTAMP:${toUtc(new Date())}`, `DTSTART:${toUtc(start)}`, `DTEND:${toUtc(end)}`,
    `SUMMARY:${EVENT_TITLE}`, `DESCRIPTION:${EVENT_DETAILS}`,
    'BEGIN:VALARM', 'TRIGGER:-PT1H', 'ACTION:DISPLAY', 'DESCRIPTION:Tu llamada con Chale es en 1 hora', 'END:VALARM',
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'llamada-con-chale.ics';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
